import { ArrowUpRight, Copy, Inbox, LoaderCircle, Pencil, Plus, Send } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, Department, Project, Session, Task, TenantSettings, User } from "../api";
import { Empty, PageHead } from "../components/ui";
import { isoDate, statusText } from "../lib/format";
import { TaskCompose } from "./TaskCompose";

export function CollaborationPage({ session, tasks, departments, users, projects, allowPastTaskDates = true, collaborationPublishPolicy, onOpenTask, onTaskPersisted, onChanged }: {
  session: Session; tasks: Task[]; departments: Department[]; users: User[]; projects: Project[]; allowPastTaskDates?: boolean; collaborationPublishPolicy?: TenantSettings["collaborationPublishPolicy"];
  onOpenTask?: (id: string) => void; onTaskPersisted?: (task: Task) => void; onChanged: (message: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"sent" | "received" | "approvals">("sent");
  const [editing, setEditing] = useState<Task | null | undefined>(undefined);
  const [copyFrom, setCopyFrom] = useState<Task | undefined>();
  const [copyLoading, setCopyLoading] = useState<string | null>(null);
  const [copyError, setCopyError] = useState("");
  async function copyTask(id: string) {
    if (copyLoading) return;
    setCopyLoading(id); setCopyError("");
    try { setCopyFrom(await api.task(id)); setEditing(null); }
    catch (reason) { setCopyError(reason instanceof Error ? reason.message : "복제할 협업 요청을 불러오지 못했습니다."); }
    finally { setCopyLoading(null); }
  }
  function closeCompose() { setEditing(undefined); setCopyFrom(undefined); }
  const departmentName = useMemo(() => new Map(departments.map((item) => [item.id, item.name])), [departments]);
  const userName = useMemo(() => new Map(users.map((item) => [item.id, item.displayName])), [users]);
  const sent = tasks.filter((item) => item.issuerId === session.userId);
  const received = tasks.filter((item) => item.issuerId !== session.userId && item.status !== "APPROVAL_PENDING");
  const approvals = tasks.filter(item => item.status === "APPROVAL_PENDING" && item.collaborationApproverId === session.userId);
  const visible = tab === "sent" ? sent : tab === "approvals" ? approvals : received;

  function openTask(item: Task) {
    if (item.issuerId === session.userId && ["DRAFT", "REJECTED"].includes(item.status)) setEditing(item);
    else onOpenTask?.(item.id);
  }

  return <>
    <PageHead title={t("collaboration.title")} meta={t("collaboration.meta")} controls={
      <button className="primary" disabled={Boolean(copyLoading)} onClick={() => { setCopyFrom(undefined); setEditing(null); }}><Plus size={15} /> {t("collaboration.create")}</button>
    } />
    <div className="collaboration-tabs" role="tablist">
      <button className={tab === "sent" ? "on" : ""} onClick={() => setTab("sent")}><Send size={15} /> {t("collaboration.sent")} <em>{sent.length}</em></button>
      <button className={tab === "received" ? "on" : ""} onClick={() => setTab("received")}><Inbox size={15} /> {t("collaboration.received")} <em>{received.length}</em></button>
      <button className={tab === "approvals" ? "on" : ""} onClick={() => setTab("approvals")}>{t("collaboration.approvals")} <em>{approvals.length}</em></button>
    </div>
    {copyError && <p className="global-error" role="alert">{copyError}</p>}
    <section className="table-card collaboration-table">
      <div className="collaboration-row collaboration-head">
        <span>{t("collaboration.status")}</span><span>{t("collaboration.task")}</span><span>{t(tab === "sent" ? "collaboration.requestedTeam" : "collaboration.senderTeam")}</span><span>{t("collaboration.assignee")}</span><span>{t("collaboration.due")}</span><span>{t("collaboration.action")}</span>
      </div>
      {visible.map((item) => <article className="collaboration-row" key={item.id}>
        <span><i className={`collaboration-state state-${item.status.toLowerCase()}`} />{t(`status.${item.status}`, { defaultValue: statusText[item.status] })}</span>
        <span className="title-cell"><b>{item.internalTitle}</b><small>{item.background || t("collaboration.noDescription")}</small></span>
        <span>{departmentName.get(tab === "sent" ? item.departmentId : item.collaborationSourceDepartmentId || "") || t("collaboration.team")}</span>
        <span>{item.assigneeIds[0] ? userName.get(item.assigneeIds[0]) || t("collaboration.member") : <em className="muted">{t("collaboration.unassigned")}</em>}</span>
        <span className="mono">{isoDate(item.dueOn) || t("collaboration.unassigned")}</span>
        <span className="collaboration-row-action"><button type="button" disabled={Boolean(copyLoading)} onClick={() => openTask(item)}>
          {item.issuerId === session.userId && ["DRAFT", "REJECTED"].includes(item.status)
            ? <><Pencil size={14} /> {t("collaboration.editDraft")}</>
            : <><ArrowUpRight size={14} /> {t("collaboration.openTask")}</>}
        </button><button type="button" disabled={Boolean(copyLoading)} onClick={() => void copyTask(item.id)}>
          {copyLoading === item.id ? <LoaderCircle size={14} className="spin" /> : <Copy size={14} />}복제
        </button></span>
      </article>)}
      {!visible.length && <Empty title={t(tab === "approvals" ? "collaboration.noApprovals" : tab === "sent" ? "collaboration.noSent" : "collaboration.noReceived")} copy={t(tab === "approvals" ? "collaboration.noApprovalsHelp" : tab === "sent" ? "collaboration.noSentHelp" : "collaboration.noReceivedHelp")} />}
    </section>

    {editing !== undefined && <TaskCompose collaboration collaborationPublishPolicy={collaborationPublishPolicy} allowPastTaskDates={allowPastTaskDates} session={session} task={editing || undefined} seed={copyFrom ? { copyFrom } : undefined}
      departments={departments} users={users} projects={projects} onClose={closeCompose}
      onPersisted={onTaskPersisted}
      onDeleted={() => { void onChanged("초안을 삭제했습니다."); }}
      onCreated={async (task, message) => {
        onTaskPersisted?.(task); closeCompose(); await onChanged(message);
      }} />}
  </>;
}
